import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, type LinkProps } from 'react-router-dom';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useHasHover } from '@/hooks/use-has-hover';
import { useIsTruncated } from '@/hooks/use-is-truncated';
import { cn } from '@/lib/utils';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';

export const DEFAULT_MAX_LENGTH = 50;
export const DEFAULT_START_LENGTH = 3;
export const DEFAULT_END_LENGTH = 4;
export const TRUNCATE_DEFAULT_MODE = 'slice' as const satisfies 'slice' | 'css';
export const TRUNCATE_CSS_CLASS = 'truncate block';
// `max-w-full` (instead of `w-full`) caps the trigger at the parent width but
// lets it shrink to its content width — so when the truncated text is short,
// the copy button sits right next to it instead of being pushed to the far
// right edge of an over-wide container.
export const TRUNCATE_CSS_CONTAINER_CLASS = 'min-w-0 max-w-full';
// `inline-flex` makes the trigger size to content (with the cap above),
// putting text + copy button adjacent. `gap-2` keeps a comfortable separation.
export const CONTAINER_BASE_CLASS = 'inline-flex items-center gap-2';
export const ICON_CLASS =
    'text-muted-foreground hover:text-primary h-4 w-4 shrink-0 cursor-pointer';

type TruncateLineCount = 1 | 2 | 3 | 4 | 5;

// `wrap-anywhere` ensures that even unbroken strings (e.g., a 200-character
// keyboard mash with no spaces) wrap inside the container so `line-clamp`
// can actually clamp at the configured line count instead of producing one
// extra-wide line that overflows horizontally.
//
// It must be `wrap-anywhere` (`overflow-wrap: anywhere`), not `wrap-break-word`:
// only `anywhere` also shrinks the element's intrinsic min-content width, which
// is what stops an unbreakable string from widening a flex/grid parent in the
// first place. Tailwind v3's `break-words` — used here previously — is not a
// utility in v4 at all and compiled to nothing, so this never took effect.
const MULTILINE_TRUNCATE_CLASSES: Record<TruncateLineCount, string> = {
    1: TRUNCATE_CSS_CLASS,
    2: 'block line-clamp-2 wrap-anywhere',
    3: 'block line-clamp-3 wrap-anywhere',
    4: 'block line-clamp-4 wrap-anywhere',
    5: 'block line-clamp-5 wrap-anywhere',
};

interface TruncateProps {
    text: string;
    /**
     * Optional override for what gets written to the clipboard when the
     * copy button is clicked. Defaults to `text`. Useful when the display
     * value is human-readable but the value worth copying is augmented
     * (e.g. a public URL the consumer wants UTM-tagged on copy).
     */
    copyText?: string;
    maxLength?: number;
    className?: string;
    showTooltip?: boolean;
    showCopyButton?: boolean;
    startLength?: number;
    endLength?: number;
    /**
     * Renders the (truncated) text as a router link to this path instead of
     * plain text — for name cells that both identify and navigate to a record.
     * The anchor carries the app's standard link affordance (pointer cursor,
     * hover underline) and stops the click from reaching a clickable row, so a
     * single click navigates once rather than twice.
     *
     * Reveal stays available: a linked value is still hover-revealed like any
     * other, but only on hover — the click belongs to the link.
     */
    path?: string;
    /**
     * Optional state to pass to the underlying React Router Link component.
     */
    linkState?: LinkProps['state'];
    truncateMode?: 'slice' | 'css';
    lines?: TruncateLineCount;
    /**
     * How the hidden value is revealed.
     *
     * - `'hover-and-click'` (default) — hover on pointer devices, tap-to-open
     *   Popover on touch, and a click affordance on both.
     * - `'hover'` — reveal on hover only, never intercepting pointer input.
     *   Required inside a listbox option (a `ComboboxItem`, `SelectItem` or
     *   `DropdownMenuItem`): there the click belongs to the option's own
     *   selection gesture, and the default's `stopPropagation` would swallow it
     *   so the option could never be selected. Touch devices get no reveal in
     *   this mode — tapping selects, which is the more important action.
     */
    revealOn?: 'hover-and-click' | 'hover';
}

export function Truncate({
    text,
    copyText,
    maxLength = DEFAULT_MAX_LENGTH,
    className,
    startLength = DEFAULT_START_LENGTH,
    endLength = DEFAULT_END_LENGTH,
    showTooltip = true,
    showCopyButton = false,
    path = '',
    linkState,
    truncateMode = TRUNCATE_DEFAULT_MODE,
    lines = 1,
    revealOn = 'hover-and-click',
}: TruncateProps) {
    const { t } = useTranslation();
    const hasHover = useHasHover();
    // Passive mode keeps every pointer gesture for whatever owns the row — no
    // click handler, no touch Popover, no pointer-catching overlay. A `path`
    // implies it: the click is the link's, so the reveal has to settle for
    // hover (and a tap navigates, which is what a tap on a link should do).
    const isPassive = revealOn === 'hover' || Boolean(path);
    const isTruncated = text.length > maxLength;
    // `slice` mode decides up front from the length budget; `css` mode has to
    // measure the rendered element, because whether the ellipsis engages
    // depends on the column width, not on the string length.
    const { ref: textRef, measure } = useIsTruncated<HTMLElement>();
    // Either branch below renders the text, so either has to be measurable. A
    // `RefObject` is invariant in its element type and would only type-check
    // against one of `<span>` / `<Link>`; a callback ref fits both.
    const setTextNode = React.useCallback(
        (node: HTMLElement | null) => {
            textRef.current = node;
        },
        [textRef],
    );
    const [isOverlayOpen, setIsOverlayOpen] = React.useState(false);
    const isMultiline = truncateMode === 'css' && lines > 1;
    const sliceTail = endLength > 0 ? text.slice(-endLength) : '';
    const displayText =
        truncateMode === 'slice' && isTruncated
            ? `${text.slice(0, startLength)}...${sliceTail}`
            : text;

    const textClassName = cn(
        truncateMode === 'css' && MULTILINE_TRUNCATE_CLASSES[lines],
        // Force the text element to be a shrinkable flex item so the underlying
        // `truncate` (white-space:nowrap + overflow:hidden + text-overflow:ellipsis)
        // can engage. Without `min-w-0`, the default `min-width: auto` on flex
        // items would keep the element at its intrinsic content width and the
        // ellipsis never appears in narrow containers (e.g. table cells).
        truncateMode === 'css' && 'min-w-0 flex-1',
        // A link keeps its own affordance (pointer cursor from the UA, plus the
        // underline below); anything else is inert text and says so.
        path ? 'hover:underline' : 'cursor-default',
    );

    const containerClassName = cn(
        isMultiline ? TRUNCATE_CSS_CONTAINER_CLASS : CONTAINER_BASE_CLASS,
        truncateMode === 'css' && TRUNCATE_CSS_CONTAINER_CLASS,
        className,
    );

    const copyToClipboard = async (e: React.MouseEvent) => {
        e.stopPropagation();
        await navigator.clipboard.writeText(copyText ?? text);
        toast.success(t('common.copiedToClipboard', 'Copied to clipboard'));
    };

    const innerContent = (
        <>
            {path ? (
                // `stopPropagation` (not `preventDefault`) keeps a clickable
                // ancestor row from navigating a second time while leaving the
                // router's own click handling on this anchor intact.
                <Link
                    to={path}
                    state={linkState}
                    ref={setTextNode}
                    onClick={(e) => e.stopPropagation()}
                    className={textClassName}
                >
                    {displayText}
                </Link>
            ) : (
                <span ref={setTextNode} className={textClassName}>
                    {displayText}
                </span>
            )}
            {showCopyButton && <Copy className={ICON_CLASS} onClick={copyToClipboard} />}
        </>
    );

    // Consumer-level opt-out: `showTooltip: false` disables the affordance
    // outright, so no overlay is wired up and the plain container styling
    // applies. A `path` no longer opts out — a linked value is exactly the kind
    // that gets clipped in a table cell, and hiding its reveal left the full
    // name unreadable. `isPassive` above keeps the click with the link.
    const overlayEnabled = showTooltip;

    if (!overlayEnabled) {
        return (
            <div
                className={cn(
                    containerClassName,
                    !className?.includes('gap-') && 'gap-3',
                    'rounded-md',
                )}
            >
                {innerContent}
            </div>
        );
    }

    // The trigger stays mounted whether or not anything is hidden, and the
    // gate is applied to *opening* instead. Swapping the DOM on the
    // measurement would unmount the element mid-interaction and swallow the
    // pointerenter that should have opened the overlay — the reason a long
    // value could silently refuse to show its tooltip on first hover.
    //
    // Measuring here (rather than trusting the mount-time reading) keeps the
    // decision correct after web fonts swap and column widths settle, neither
    // of which resizes the element itself.
    const revealIfHidden = (next: boolean) => {
        if (!next) {
            setIsOverlayOpen(false);
            return;
        }
        setIsOverlayOpen(truncateMode === 'css' ? measure() : isTruncated);
    };

    // Pointer devices get hover *and* click: hovering a dense table row is
    // fiddly, and a click is the gesture people reach for when a value is
    // visibly cut off.
    const openOnClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        revealIfHidden(true);
    };

    // Branch on input modality so each device gets the right affordance:
    //   - Mouse-capable (desktop): hover-to-open Tooltip — feels native, no
    //     extra click required to peek at the full value.
    //   - Touch-only (mobile): tap-to-open Popover — hover doesn't exist on
    //     touch devices, so a click target is the only way to surface the
    //     truncated content.
    // `isPassive` forces the Tooltip branch even on touch: the Popover below is
    // a tap target, and inside a listbox option that tap has to select the
    // option instead. A hover tooltip is simply inert on a touch device.
    if (hasHover || isPassive) {
        return (
            <TooltipProvider>
                <Tooltip open={isOverlayOpen} onOpenChange={revealIfHidden}>
                    <TooltipTrigger asChild>
                        <div
                            // The link owns the cursor inside its own box; forcing
                            // `cursor-default` on the wrapper would leave the arrow
                            // showing over text that is clickable.
                            className={cn(containerClassName, !path && 'cursor-default')}
                            onClick={isPassive ? undefined : openOnClick}
                        >
                            {innerContent}
                        </div>
                    </TooltipTrigger>
                    {/*
                     * Override the base TooltipContent's `inline-flex w-fit max-w-xs
                     * items-center` so long content wraps cleanly:
                     *   - `block` replaces inline-flex so children flow naturally as text.
                     *   - `max-w-[min(90vw,32rem)]` lets the tooltip grow with content
                     *     up to 32rem (or 90% of the viewport on small screens).
                     *   - `whitespace-normal wrap-anywhere` forces wrapping even for
                     *     unbroken strings; the underlying CSS won't keep an
                     *     overflowing one-liner regardless of the parent layout.
                     *     `wrap-anywhere`, not `wrap-break-word` — the base sets
                     *     `w-fit`, so the box sizes to min-content, and only
                     *     `overflow-wrap: anywhere` shrinks that.
                     */}
                    <TooltipContent
                        className={cn(
                            'block max-w-[min(90vw,32rem)] text-xs whitespace-normal wrap-anywhere',
                            // The portalled tooltip can overlap the row it
                            // describes; in passive mode it must not intercept
                            // the click that row is waiting for.
                            isPassive && 'pointer-events-none',
                        )}
                    >
                        {text}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    }

    return (
        <Popover open={isOverlayOpen} onOpenChange={revealIfHidden}>
            <PopoverTrigger asChild>
                <div
                    className={cn(containerClassName, 'cursor-pointer')}
                    onClick={openOnClick}
                    role="button"
                    tabIndex={0}
                    aria-label={t('common.viewFullText', 'View full text')}
                >
                    {innerContent}
                </div>
            </PopoverTrigger>
            <PopoverContent
                className="w-auto max-w-[min(90vw,32rem)] text-sm whitespace-normal wrap-anywhere"
                onClick={(e) => e.stopPropagation()}
            >
                {text}
            </PopoverContent>
        </Popover>
    );
}
